/**
 * Adobe Analytics Variable Extractor (TRAC-465)
 *
 * Extracts Adobe Analytics s.* variables from raw TR.com HTML.
 * These are embedded in <script> blocks as `s.varName = "value"` assignments.
 *
 * Returns only variables that are present — different page types have different sets.
 */

/** All s.* variables we want to capture, mapped to readable metadata keys */
const S_VARIABLES: Record<string, string> = {
  pageName: 'pageName',
  channel: 'channel',
  prop60: 'prop60',         // device type: desktop | mobile
  prop6: 'prop6',           // language
  eVar18: 'eVar18',         // auth status: authenticated | not authenticated
  events: 'events',         // Adobe events fired on the page
  eVar61: 'eVar61',         // tire size being browsed (search results)
  eVar45: 'eVar45',         // how they got there / TDG step
  eVar59: 'eVar59',         // search method
  eVar11: 'eVar11',         // performance category filter
  prop3: 'prop3',           // result count (page:total)
  eVar44: 'eVar44',         // TDG flow marker
  prop46: 'prop46',         // TDG event marker
  pageType: 'pageType',     // page classification
};

/**
 * Extract Adobe Analytics variables from raw TR.com page HTML.
 *
 * Looks for patterns like: s.pageName="tr: tires: product details"
 * Also extracts data-order-session-id from DOM attributes.
 *
 * @returns Record with only the keys that were found (empty object if none)
 */
export function parseAdobeAnalytics(html: string): Record<string, string> {
  const result: Record<string, string> = {};

  // Extract s.* variable assignments: s.varName="value" or s.varName='value'
  for (const [sVar, key] of Object.entries(S_VARIABLES)) {
    const match = html.match(new RegExp(`s\\.${sVar}\\s*=\\s*["']([^"']+)["']`));
    if (match) {
      result[key] = match[1];
    }
  }

  // Extract orderSessionId from data attribute: data-order-session-id="xxx"
  const sessionIdMatch = html.match(/data-order-session-id\s*=\s*["']([^"']+)["']/);
  if (sessionIdMatch) {
    result.orderSessionId = sessionIdMatch[1];
  }

  return result;
}
